import { lumi } from '@/lib/lumi'
import { notifyFormSubmission } from '@/lib/notifyFormSubmission'
import { AnimatePresence, motion } from 'framer-motion'
import { useState } from 'react'
import { usePageMeta } from '@/hooks/usePageMeta'

// ─── Structure ────────────────────────────────────────────────────────────────

interface Question {
  id: string
  label: string
}

interface Block {
  title: string
  subtitle: string
  questions: Question[]
}

// Bloco 0 = identificação (nome + email), Blocos 1-5 = perguntas, Bloco 6 = Direcionamento
const BLOCKS: Block[] = [
  {
    title: 'Bloco 1',
    subtitle: 'Programação Familiar',
    questions: [
      { id: 'q1', label: 'Qual era a frase que sua mãe ou figura materna mais repetia sobre a vida, sobre o que era possível para você ou sobre o mundo?' },
      { id: 'q2', label: 'Qual era a frase do seu pai ou figura paterna sobre a vida, o trabalho ou sobre o que você poderia ser?' },
      { id: 'q3', label: 'Como era o relacionamento dos seus pais ou das figuras principais da sua família entre si? O que esse modelo te ensinou sobre relacionamentos?' },
      { id: 'q4', label: 'O que era proibido sentir ou expressar na sua família?' },
      { id: 'q5', label: 'O que você precisava fazer ou ser para se sentir amado e aceito na sua família?' },
      { id: 'q6', label: 'Qual papel você ocupava na família? O responsável, o invisível, o rebelde, o cuidador, outro?' },
    ],
  },
  {
    title: 'Bloco 2',
    subtitle: 'Primeiras Impressões sobre Si Mesmo',
    questions: [
      { id: 'q7',  label: 'Houve algum momento na infância ou adolescência em que você se sentiu verdadeiramente visto e valorizado? Como foi?' },
      { id: 'q8',  label: 'Houve alguma situação que mudou algo em você, que te fez se fechar, se diminuir ou desconfiar de si mesmo?' },
      { id: 'q9',  label: 'O que você aprendeu, explícita ou implicitamente, sobre o que você pode ser, ter ou fazer na vida?' },
      { id: 'q10', label: 'Qual era a crença da sua família sobre dinheiro e prosperidade?' },
      { id: 'q11', label: 'Como a espiritualidade ou religião era vivida na sua casa? O que isso te ensinou?' },
    ],
  },
  {
    title: 'Bloco 3',
    subtitle: 'O Corpo como Memória',
    questions: [
      { id: 'q12', label: 'Onde no corpo você sente mais tensão, bloqueio ou desconforto recorrente?' },
      { id: 'q13', label: 'Existe alguma doença, sintoma ou padrão físico que se repete na sua vida ou na sua família?' },
      { id: 'q14', label: 'Como é sua relação com seu corpo? Você se sente à vontade nele?' },
      { id: 'q15', label: 'Quando você sente medo ou ansiedade, o que acontece no seu corpo?' },
    ],
  },
  {
    title: 'Bloco 4',
    subtitle: 'Padrões que se Repetem',
    questions: [
      { id: 'q16', label: 'Qual situação ou tipo de conflito se repete na sua vida mesmo quando você tenta mudar?' },
      { id: 'q17', label: 'Em qual área da sua vida você sente mais estagnação: relacionamentos, dinheiro, saúde ou propósito?' },
      { id: 'q18', label: 'O que você mais evita sentir? Qual emoção é mais difícil para você?' },
      { id: 'q19', label: 'Quando você está prestes a conquistar algo importante, o que costuma acontecer?' },
      { id: 'q20', label: 'Qual é o pensamento mais recorrente que te sabota?' },
    ],
  },
  {
    title: 'Bloco 5',
    subtitle: 'O Momento Presente',
    questions: [
      { id: 'q21', label: 'O que te trouxe até o Protocolo de Liberação e Cocriação? O que você está buscando transformar?' },
      { id: 'q22', label: 'O que você já tentou antes para resolver isso? O que funcionou e o que não funcionou?' },
      { id: 'q23', label: 'Se esse padrão se resolver completamente, como seria sua vida daqui a um ano?' },
      { id: 'q24', label: 'Existe algo que você nunca contou a ninguém e que sente que ainda pesa no seu campo?' },
      { id: 'q25', label: 'O que você quer que eu saiba sobre você antes de criar seu Mapa de Revelações?' },
    ],
  },
]

const Q26 = {
  id: 'q26',
  label: 'De tudo que você compartilhou aqui, qual é a dor ou o padrão que você mais quer resolver agora? O que está pesando mais no seu campo neste momento?',
}

const URGENCY_OPTIONS = [
  'Financeiro e prosperidade',
  'Relacionamentos e amor',
  'Saúde e corpo',
  'Propósito e carreira',
  'Autoestima e autoimagem',
  'Espiritualidade e conexão interior',
]

// step 0 = identificação, steps 1-5 = blocos de perguntas, step 6 = bloco final
const TOTAL_STEPS = 7 // 0..6
const TOTAL_BLOCKS = 6 // blocos numerados para a barra de progresso (1-6)

// Todas as ids de texto aberto (q1..q26)
const ALL_TEXT_IDS = [
  ...BLOCKS.flatMap(b => b.questions.map(q => q.id)),
  'q26',
]

// ─── Page ────────────────────────────────────────────────────────────────────

export default function PageFormulario() {
  usePageMeta({
    title: 'Formulário do Protocolo — Reative os 4 Elementos',
    description: 'Formulário de investigação profunda do Protocolo de Liberação e Cocriação.',
    ogImage: 'https://static.lumi.new/76/768cd02825460eb1548b4156e8d0d987.svg',
    ogUrl: 'https://jornada.reacendaluz.com.br/formulario/',
  })

  const accentColor = '#B9A3D9'
  const accentSoft  = '#D4C3EA'

  const [step, setStep] = useState(0)         // 0=ID, 1-5=blocos, 6=final
  const [direction, setDirection] = useState(1) // 1 = avançar

  const [name, setName]   = useState('')
  const [email, setEmail] = useState('')
  const [answers, setAnswers] = useState<Record<string, string>>({})
  const [urgency, setUrgency] = useState<string[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted]   = useState(false)
  const [error, setError] = useState('')

  function updateAnswer(id: string, value: string) {
    setAnswers(prev => ({ ...prev, [id]: value }))
    setError('')
  }

  function toggleUrgency(opt: string) {
    setError('')
    setUrgency(prev => {
      if (prev.includes(opt)) return prev.filter(o => o !== opt)
      if (prev.length >= 2) return prev
      return [...prev, opt]
    })
  }

  // Barra de progresso: step 0 = 0 blocos completos; step N (1-6) = bloco N de 6
  const progressBlock = step === 0 ? 0 : step
  const progressPercent = (progressBlock / TOTAL_BLOCKS) * 100

  // Avança para o próximo step (com validação)
  function handleContinue() {
    setError('')

    // Step 0: validação de identificação
    if (step === 0) {
      const trimName  = name.trim()
      const trimEmail = email.trim()
      if (!trimName) {
        setError('Por favor informe seu nome.')
        document.getElementById('field-name')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        return
      }
      if (!trimEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimEmail)) {
        setError('Por favor informe um email válido.')
        document.getElementById('field-email')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        return
      }
      advance()
      return
    }

    // Steps 1-5: validação das perguntas do bloco
    if (step >= 1 && step <= 5) {
      const block = BLOCKS[step - 1]
      for (const q of block.questions) {
        if (!(answers[q.id] || '').trim()) {
          setError('Por favor responda todas as perguntas deste bloco antes de continuar.')
          document.getElementById(`field-${q.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
          return
        }
      }
      advance()
      return
    }

    // Step 6: bloco final — submeter
    handleSubmit()
  }

  function advance() {
    setDirection(1)
    window.scrollTo({ top: 0, behavior: 'smooth' })
    setStep(prev => prev + 1)
  }

  async function handleSubmit() {
    // Valida Q26
    if (!(answers['q26'] || '').trim()) {
      setError('Por favor responda a pergunta 26 antes de enviar.')
      document.getElementById('field-q26')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }
    if (urgency.length === 0) {
      setError('Escolha pelo menos uma área de urgência (até 2).')
      document.getElementById('field-q27')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }

    setSubmitting(true)
    try {
      const now = new Date().toISOString()
      const payload: Record<string, unknown> = {
        name:  name.trim(),
        email: email.trim(),
        q27:   urgency,
        createdAt: now,
        updatedAt: now,
      }
      for (const id of ALL_TEXT_IDS) {
        payload[id] = (answers[id] || '').trim()
      }
      await lumi.entities.formulario_protocolo.create(payload)
      notifyFormSubmission({ name: name.trim(), formName: 'Ciclo 1', submittedAt: now })
      setSubmitted(true)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch (err) {
      console.error('Erro ao enviar formulário:', err)
      setError('Não foi possível enviar agora. Tente novamente em instantes.')
    } finally {
      setSubmitting(false)
    }
  }

  // ─── Success ───────────────────────────────────────────────────────────────

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0a0f14] flex items-center justify-center px-5 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9 }}
          className="max-w-xl w-full text-center"
        >
          <p className="text-[10px] tracking-[0.4em] uppercase font-body mb-6" style={{ color: accentSoft }}>
            Protocolo de Liberação e Cocriação
          </p>

          <div
            className="w-16 h-16 rounded-full mx-auto mb-8 flex items-center justify-center"
            style={{ background: `${accentColor}18`, border: `1px solid ${accentSoft}55` }}
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke={accentSoft}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>

          <h1 className="font-display type-hero mb-6" style={{ color: 'var(--ink,#e8e0d5)' }}>
            Obrigada por{' '}
            <em className="italic font-light" style={{ color: accentSoft }}>
              compartilhar
            </em>{' '}
            com tanta profundidade
          </h1>

          <p className="font-body type-body mb-5" style={{ color: 'rgba(232,224,213,0.75)' }}>
            Suas respostas foram recebidas com muito cuidado.
          </p>
          <p className="font-body type-body" style={{ color: 'rgba(232,224,213,0.65)' }}>
            Você receberá sua entrega em até <span className="numerals">72</span> horas.
            Nos períodos de maior volume pode ser em até <span className="numerals">5</span> dias úteis.
          </p>

          <div
            className="h-px w-24 mx-auto mt-12"
            style={{ background: `linear-gradient(90deg, transparent, ${accentSoft}66, transparent)` }}
          />
          <p className="text-[10px] tracking-[0.3em] uppercase font-body mt-8" style={{ color: 'rgba(232,224,213,0.45)' }}>
            Mônica — Reacenda Luz
          </p>
        </motion.div>
      </div>
    )
  }

  // ─── Form ──────────────────────────────────────────────────────────────────

  const isLastStep = step === 6

  return (
    <div className="min-h-screen bg-[#0a0f14]">
      <div className="max-w-2xl mx-auto px-5 sm:px-6 py-14 sm:py-20">

        {/* ── Header fixo ── */}
        <motion.header
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-10 sm:mb-14"
        >
          <p className="text-[10px] tracking-[0.4em] uppercase font-body mb-5" style={{ color: accentSoft }}>
            Protocolo de Liberação e Cocriação
          </p>
          <h1 className="font-display type-hero mb-6" style={{ color: 'var(--ink,#e8e0d5)' }}>
            Seu Mapa de Revelações{' '}
            <em className="italic font-light" style={{ color: accentSoft }}>
              começa aqui
            </em>
          </h1>
          <p className="font-body type-body" style={{ color: 'rgba(232,224,213,0.7)', lineHeight: 1.8 }}>
            Responda com calma e honestidade.
            Não existe resposta certa ou errada.
            Quanto mais você se abrir, mais profunda será sua entrega.
          </p>
          <div
            className="h-px w-24 mx-auto mt-10"
            style={{ background: `linear-gradient(90deg, transparent, ${accentSoft}66, transparent)` }}
          />
        </motion.header>

        {/* ── Barra de progresso ── */}
        {step > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-10"
          >
            {/* Label */}
            <div className="flex items-center justify-between mb-3">
              <p className="text-[11px] tracking-[0.3em] uppercase font-body" style={{ color: accentSoft }}>
                Bloco{' '}
                <span className="numerals">{progressBlock}</span>
                {' '}de{' '}
                <span className="numerals">{TOTAL_BLOCKS}</span>
              </p>
              <p className="text-[11px] font-body numerals" style={{ color: 'rgba(232,224,213,0.4)' }}>
                {Math.round(progressPercent)}%
              </p>
            </div>

            {/* Track */}
            <div
              className="w-full h-[2px] rounded-full overflow-hidden"
              style={{ background: 'rgba(255,255,255,0.07)' }}
            >
              <motion.div
                className="h-full rounded-full"
                style={{ background: `linear-gradient(90deg, ${accentColor}, ${accentSoft})` }}
                initial={{ width: `${((progressBlock - 1) / TOTAL_BLOCKS) * 100}%` }}
                animate={{ width: `${progressPercent}%` }}
                transition={{ duration: 0.6, ease: 'easeOut' }}
              />
            </div>

            {/* Dots */}
            <div className="flex justify-between mt-2">
              {Array.from({ length: TOTAL_BLOCKS }).map((_, i) => (
                <div
                  key={i}
                  className="w-1.5 h-1.5 rounded-full transition-all duration-300"
                  style={{
                    background: i < progressBlock ? accentColor : 'rgba(255,255,255,0.15)',
                  }}
                />
              ))}
            </div>
          </motion.div>
        )}

        {/* ── Conteúdo do step (animado) ── */}
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={step}
            custom={direction}
            variants={{
              enter: (d: number) => ({ opacity: 0, x: d > 0 ? 40 : -40 }),
              center: { opacity: 1, x: 0 },
              exit:  (d: number) => ({ opacity: 0, x: d > 0 ? -40 : 40 }),
            }}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.45, ease: 'easeInOut' }}
          >

            {/* STEP 0 — Identificação */}
            {step === 0 && (
              <section className="space-y-5">
                <div className="mb-8">
                  <p className="text-[10px] tracking-[0.4em] uppercase font-body mb-3" style={{ color: accentSoft }}>
                    Antes de começar
                  </p>
                  <h2 className="font-display type-subtitle" style={{ color: 'var(--ink,#e8e0d5)' }}>
                    <em className="italic font-light">Quem é você?</em>
                  </h2>
                  <div
                    className="h-px w-16 mt-5"
                    style={{ background: `linear-gradient(90deg, ${accentSoft}55, transparent)` }}
                  />
                </div>

                <TextInput
                  id="field-name"
                  label="Seu nome completo"
                  value={name}
                  onChange={v => { setName(v); setError('') }}
                  accentSoft={accentSoft}
                />
                <TextInput
                  id="field-email"
                  label="Seu email"
                  type="email"
                  value={email}
                  onChange={v => { setEmail(v); setError('') }}
                  accentSoft={accentSoft}
                />
              </section>
            )}

            {/* STEPS 1-5 — Blocos de perguntas */}
            {step >= 1 && step <= 5 && (() => {
              const block = BLOCKS[step - 1]
              // offset global de numeração
              const offset = BLOCKS.slice(0, step - 1).reduce((s, b) => s + b.questions.length, 0)
              return (
                <section>
                  <div className="mb-10 text-center sm:text-left">
                    <p
                      className="text-[10px] tracking-[0.4em] uppercase font-body mb-2 numerals"
                      style={{ color: accentSoft }}
                    >
                      {block.title}
                    </p>
                    <h2 className="font-display type-subtitle" style={{ color: 'var(--ink,#e8e0d5)' }}>
                      <em className="italic font-light">{block.subtitle}</em>
                    </h2>
                    <div
                      className="h-px w-16 mt-5 mx-auto sm:mx-0"
                      style={{ background: `linear-gradient(90deg, ${accentSoft}55, transparent)` }}
                    />
                  </div>

                  <div className="space-y-8">
                    {block.questions.map((q, qIdx) => (
                      <QuestionField
                        key={q.id}
                        id={q.id}
                        number={offset + qIdx + 1}
                        label={q.label}
                        value={answers[q.id] || ''}
                        onChange={v => updateAnswer(q.id, v)}
                        accentSoft={accentSoft}
                      />
                    ))}
                  </div>
                </section>
              )
            })()}

            {/* STEP 6 — Bloco Final */}
            {step === 6 && (
              <section>
                <div className="mb-10 text-center sm:text-left">
                  <p className="text-[10px] tracking-[0.4em] uppercase font-body mb-2" style={{ color: accentSoft }}>
                    Bloco Final
                  </p>
                  <h2 className="font-display type-subtitle" style={{ color: 'var(--ink,#e8e0d5)' }}>
                    <em className="italic font-light">Direcionamento</em>
                  </h2>
                  <div
                    className="h-px w-16 mt-5 mx-auto sm:mx-0"
                    style={{ background: `linear-gradient(90deg, ${accentSoft}55, transparent)` }}
                  />
                </div>

                <div className="space-y-10">
                  {/* Q26 */}
                  <QuestionField
                    id={Q26.id}
                    number={26}
                    label={Q26.label}
                    value={answers[Q26.id] || ''}
                    onChange={v => updateAnswer(Q26.id, v)}
                    accentSoft={accentSoft}
                  />

                  {/* Q27 — Seleção múltipla */}
                  <div id="field-q27">
                    <div className="flex items-baseline gap-3 mb-4">
                      <span className="numerals text-[12px] tracking-[0.2em] font-body" style={{ color: accentSoft }}>
                        27
                      </span>
                      <p className="font-body type-body" style={{ color: 'var(--ink,#e8e0d5)', lineHeight: 1.6 }}>
                        Em qual área da sua vida você sente mais urgência de transformação agora?
                      </p>
                    </div>
                    <p className="text-[12px] italic font-body mb-4 ml-8" style={{ color: 'rgba(232,224,213,0.55)' }}>
                      Seleção múltipla — escolha até <span className="numerals">2</span>
                    </p>

                    <div className="grid gap-3 ml-0 sm:ml-8">
                      {URGENCY_OPTIONS.map(opt => {
                        const selected = urgency.includes(opt)
                        const disabled = !selected && urgency.length >= 2
                        return (
                          <button
                            key={opt}
                            type="button"
                            onClick={() => toggleUrgency(opt)}
                            disabled={disabled}
                            className="flex items-center gap-3 px-4 py-4 rounded-sm font-body text-left transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed active:scale-[0.99]"
                            style={{
                              background: selected ? `${accentColor}14` : 'rgba(255,255,255,0.03)',
                              border: selected ? `1px solid ${accentSoft}66` : '1px solid rgba(255,255,255,0.1)',
                              color: selected ? accentSoft : 'var(--ink,#e8e0d5)',
                            }}
                          >
                            <span
                              className="w-4 h-4 rounded-sm flex-shrink-0 flex items-center justify-center transition-colors"
                              style={{
                                background: selected ? accentColor : 'transparent',
                                border: `1px solid ${selected ? accentColor : 'rgba(255,255,255,0.25)'}`,
                              }}
                            >
                              {selected && (
                                <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="#0a0f14">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </span>
                            <span className="text-[15px] sm:text-[16px]">{opt}</span>
                          </button>
                        )
                      })}
                    </div>
                  </div>
                </div>
              </section>
            )}

          </motion.div>
        </AnimatePresence>

        {/* ── Erro + Botão de ação ── */}
        <div className="mt-10 pt-4">
          {error && (
            <motion.p
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-[13px] font-body text-center mb-4"
              style={{ color: '#f87171' }}
            >
              {error}
            </motion.p>
          )}
          <button
            type="button"
            onClick={handleContinue}
            disabled={submitting}
            className="block w-full text-center btn-primary px-4 sm:px-8 py-5 min-h-[56px] rounded-sm type-button tracking-[0.12em] sm:tracking-[0.18em] uppercase disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting
              ? 'Enviando...'
              : isLastStep
                ? 'Enviar minhas respostas'
                : 'Continuar'}
          </button>

          {isLastStep && (
            <p className="text-[11px] font-body text-center mt-6 italic" style={{ color: 'rgba(232,224,213,0.45)' }}>
              Suas respostas são tratadas com total sigilo.
            </p>
          )}
        </div>

        {/* ── Footer ── */}
        <footer className="mt-16 text-center">
          <div
            className="h-px w-24 mx-auto mb-6"
            style={{ background: `linear-gradient(90deg, transparent, ${accentSoft}44, transparent)` }}
          />
          <p className="text-[10px] tracking-[0.3em] uppercase font-body" style={{ color: 'rgba(232,224,213,0.45)' }}>
            Reacenda Luz · Mônica Maria
          </p>
        </footer>

      </div>
    </div>
  )
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function TextInput({
  id, label, value, onChange, accentSoft, type = 'text',
}: {
  id: string
  label: string
  value: string
  onChange: (v: string) => void
  accentSoft: string
  type?: string
}) {
  return (
    <div id={id}>
      <label
        className="block text-[10px] tracking-[0.3em] uppercase font-body mb-2"
        style={{ color: accentSoft }}
      >
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        className="input-field w-full rounded-sm px-4 py-3 font-body text-[16px]"
        style={{
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.1)',
          color: 'var(--ink,#e8e0d5)',
        }}
      />
    </div>
  )
}

function QuestionField({
  id, number, label, value, onChange, accentSoft,
}: {
  id: string
  number: number
  label: string
  value: string
  onChange: (v: string) => void
  accentSoft: string
}) {
  return (
    <div id={`field-${id}`}>
      <div className="flex items-baseline gap-3 mb-3">
        <span className="numerals text-[12px] tracking-[0.2em] font-body flex-shrink-0" style={{ color: accentSoft }}>
          {String(number).padStart(2, '0')}
        </span>
        <label
          htmlFor={id}
          className="font-body type-body"
          style={{ color: 'var(--ink,#e8e0d5)', lineHeight: 1.6 }}
        >
          {label}
        </label>
      </div>
      <textarea
        id={id}
        value={value}
        onChange={e => onChange(e.target.value)}
        rows={5}
        className="input-field w-full rounded-sm px-4 py-3 font-body text-[16px] resize-y"
        style={{
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.1)',
          color: 'var(--ink,#e8e0d5)',
          lineHeight: 1.7,
          minHeight: 120,
        }}
        placeholder="Escreva sua resposta aqui..."
      />
    </div>
  )
}
